import DateFnsUtils from '@date-io/date-fns';
import {DateTimeFormatter} from '@js-joda/core';
import {Locale} from "@js-joda/locale_en-us";
import {MuiPickersUtilsProvider} from '@material-ui/pickers';
import enLocale from "date-fns/locale/en-US";
import deepmerge from 'deepmerge';
import {useSnackbar, WithSnackbarProps} from 'notistack';
import React, {createContext, Dispatch, ReactNode, useContext, useEffect, useReducer, useState} from 'react';
import useApiFetch, {apiEndpoint, ApiFetch} from '../Auth/Api';
import FullPageLoadingIndicator from '../LoadingIndicator/FullPageLoadingIndicator';

export type FirstDayOfWeek = 0 | 1 | 2 | 3 | 4 | 5 | 6;
export type RoundToNearest = 5 | 6 | 10 | 15;
export type DefaultTimeInput = 'range' | 'duration';
export type InitialRange = 'this-week' | 'yesterday' | 'today';

export type BookmarkLink = {
    url : string;
    title : string;
    openInNew ?: boolean;
    favorite ?: boolean;
};

export type BookmarkLinkGroup = {
    name : string;
    links : BookmarkLink[];
};

export type Preferences = {
    dashboard : {
        linkGroups : BookmarkLinkGroup[];
    };
    locale : {
        useAmPmTime : boolean;
        useIsoDate : boolean;
        firstDayOfWeek : FirstDayOfWeek;
    };
    timeEntry : {
        roundToNearest : RoundToNearest;
        defaultTimeInput : DefaultTimeInput;
        enterProjectAndComponent : boolean;
        initialRange : InitialRange;
        showProjectAndComponent : boolean;
        showTaskId : boolean;
        keepTimesOnDuplicate : boolean;
        alwaysUseFromDateForNewEntries : boolean;
        warnLowHoursThisWeek : boolean;
    };
    scheduler : {
        favoriteEmployees : number[];
        selectedPracticeAreas : string[];
        selectedTeams : number[];
        onlyCstMembers : boolean;
        showMonthlyCapacityTotals : boolean;
    };
}

export type PartialPreferences = {
    [P in keyof Preferences] ?: Partial<Preferences[P]>;
};

export type PreferencesReset = {
    [P in keyof Preferences] ?: Array<keyof Preferences[P]>;
};

const INIT_PREFERENCES = 'INIT_PREFERENCES';
const UPDATE_PREFERENCES = 'UPDATE_PREFERENCES';

type PreferencesState = {
    preferences : Preferences;
    i18n : I18n;
};

type InitPreferenceAction = {
    type : typeof INIT_PREFERENCES;
    payload : {
        preferences : Preferences;
    };
};

type UpdatePreferenceAction = {
    type : typeof UPDATE_PREFERENCES;
    payload : {
        update : PartialPreferences;
    };
};

type Actions = InitPreferenceAction | UpdatePreferenceAction;

const preferencesReducer = (state : PreferencesState | null, action : Actions) : PreferencesState | null => {
    switch (action.type) {
        case INIT_PREFERENCES:
            return {
                preferences: action.payload.preferences,
                i18n: inferI18n(action.payload.preferences),
            };

        case UPDATE_PREFERENCES: {
            if (!state) {
                throw new Error('Update called before initial load');
            }

            const preferences = deepmerge<Preferences, PartialPreferences>(
                state.preferences,
                action.payload.update,
                {arrayMerge: (target, source) => source}
            );

            return {
                preferences,
                i18n: inferI18n(preferences),
            };
        }

        default:
            return state;
    }
};

type Props = {
    children : ReactNode;
    categories : Array<keyof Preferences>;
};

const PreferencesStateContext = createContext<PreferencesState | null>(null);
const PreferencesDispatchContext = createContext<Dispatch<Actions> | null>(null);

type I18n = {
    locale : Locale;
    inputDateFormat : string;
    timeFormatter : DateTimeFormatter;
};

const inferI18n = (preferences : Preferences) : I18n => {
    if (!preferences.locale) {
        return {
            locale: enLocale,
            inputDateFormat: 'MM/dd/yyyy',
            timeFormatter: DateTimeFormatter.ofPattern('h:mm a').withLocale(Locale.US),
        }
    }

    const locale = {...enLocale};

    if (!locale.options) {
        locale.options = {};
    }

    locale.options.weekStartsOn = preferences.locale.firstDayOfWeek;

    return {
        locale,
        inputDateFormat: preferences.locale.useIsoDate ? 'yyyy-MM-dd' : 'MM/dd/yyyy',
        timeFormatter: DateTimeFormatter.ofPattern(
            preferences.locale.useAmPmTime ? 'h:mm a' : 'HH:mm',
        ).withLocale(Locale.US),
    };
};

const PreferencesProvider : React.FC<Props> = ({children, categories} : Props) => {
    const [loading, setLoading] = useState(false);
    const [state, dispatch] = useReducer(preferencesReducer, null);
    const snackbar = useSnackbar();
    const apiFetch = useApiFetch();

    useEffect(() => {
        if (loading || state) {
            return;
        }

        setLoading(true);

        (async () => {
            const url = new URL('/v1/preferences', apiEndpoint);

            for (const category of categories) {
                url.searchParams.append('category', category);
            }

            try {
                const result = await apiFetch(url.href);

                if (!result.ok) {
                    snackbar.enqueueSnackbar('Could not load preferences.', {variant: 'error'});
                    return;
                }

                const data : Preferences = await result.json();

                dispatch({
                    type: INIT_PREFERENCES,
                    payload: {preferences: data},
                });
            } catch (e) {
                snackbar.enqueueSnackbar('Could not load preferences.', {variant: 'error'});
            }
        })();
    }, [dispatch, snackbar, loading, state, categories, apiFetch]);

    if (!state) {
        return <FullPageLoadingIndicator/>;
    }

    return (
        <PreferencesStateContext.Provider value={state}>
            <PreferencesDispatchContext.Provider value={dispatch}>
                <MuiPickersUtilsProvider utils={DateFnsUtils} locale={state.i18n.locale}>
                    {children}
                </MuiPickersUtilsProvider>
            </PreferencesDispatchContext.Provider>
        </PreferencesStateContext.Provider>
    );
};

export const usePreferencesState = () : PreferencesState => {
    const context = useContext(PreferencesStateContext);

    if (!context) {
        throw new Error('Context was used before initial load');
    }

    return context;
};

let queue : PartialPreferences = {};
let updatePromise : Promise<unknown> | null = null;
let updateQueued = false;

type UpdatePreferences = (update : PartialPreferences) => Promise<void>;

export const useUpdatePreferences = () : UpdatePreferences => {
    const dispatch = usePreferencesDispatch();
    const snackbar = useSnackbar();
    const apiFetch = useApiFetch();

    return async (update : PartialPreferences) => {
        dispatch({
            type: UPDATE_PREFERENCES,
            payload: {update},
        });

        queue = deepmerge(queue, update, {arrayMerge: (target, source) => source});

        if (updateQueued) {
            return;
        }

        if (!updatePromise) {
            return performUpdate(snackbar, apiFetch);
        }

        updateQueued = true;
        await updatePromise;
        updateQueued = false;
        return performUpdate(snackbar, apiFetch);
    };
};

const performUpdate = async (snackbar : WithSnackbarProps, apiFetch : ApiFetch) => {
    const url = new URL('/v1/preferences', apiEndpoint);
    updatePromise = apiFetch(url.href, {
        method: 'PATCH',
        body: JSON.stringify(queue),
    });
    queue = {};

    try {
        await updatePromise;
    } catch (e) {
        snackbar.enqueueSnackbar('Could not update preferences.', {variant: 'error'});
    }

    updatePromise = null;
};

type ResetPreferences = (reset : PreferencesReset) => Promise<void>;

export const useResetPreferences = () : ResetPreferences => {
    const apiFetch = useApiFetch();
    const dispatch = usePreferencesDispatch();
    const snackbar = useSnackbar();

    return async (reset : PreferencesReset) => {
        if (updatePromise) {
            await updatePromise;
        }

        if (updateQueued) {
            // We resolved first, so we have to wait a moment for the next update.
            await new Promise(resolve => window.requestAnimationFrame(resolve));

            if (updatePromise) {
                await updatePromise;
            }
        }

        // At this point, no further update should be running.
        const url = new URL('/v1/preferences/reset', apiEndpoint);
        let response;

        try {
            response = await apiFetch(url.href, {
                method: 'PATCH',
                body: JSON.stringify(reset),
            });
        } catch (e) {
            snackbar.enqueueSnackbar('Could not reset preferences.', {variant: 'error'});
            return;
        }

        const update = await response.json();
        dispatch({
            type: UPDATE_PREFERENCES,
            payload: {update},
        });
    };
};

const usePreferencesDispatch = () => {
    const context = useContext(PreferencesDispatchContext);

    if (!context) {
        throw new Error('Context was used before initial load');
    }

    return context;
};

export default PreferencesProvider;
